"use client";

import React from "react";
import { Activity, ArrowRight, Timer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const programs = [{
  id: "01",
  title: "The Reserve Flight",
  description: "An intimate exploration of our limited-edition vintages, guided by our senior sommelier through the history of our most prestigious blocks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683378/img/reserve_wine_tasting.png",
  duration: "90 minutes",
  level: "$75 per guest",
  link: "/TastingToursPage"
}, {
  id: "02",
  title: "Cellar Master’s Secret",
  description: "Go behind the heavy oak doors into our private aging caves. Sample directly from the barrel and learn the alchemy of Velvet Vineyards.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683378/img/cellar_barrel_tour.png",
  duration: "2 hours",
  level: "$120 per guest",
  link: "/TastingToursPage"
}, {
  id: "03",
  title: "Estate Heritage Walk",
  description: "A scenic journey through the vines followed by a curated pairing of five signature wines with artisanal local cheeses and charcuterie.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683378/img/vineyard_heritage_walk.png",
  duration: "3 hours",
  level: "$150 per guest",
  link: "/TastingToursPage"
}];
export default function TourPackages() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="233818" id="tour-packages" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 max-w-2xl">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-widest mb-6">
            Curated <span className="text-secondary">Tour Packages</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans font-light leading-relaxed">
            Immerse yourself in the art of viticulture. From candlelit cellar secrets to sun-drenched vineyard walks, discover the soul of Velvet Vineyards.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {programs.map((program, i) => <div data-index={i} key={program.id} className="h-full">
              <Card className="group relative h-full flex flex-col overflow-hidden bg-card border-border hover:border-secondary hover:ring-1 hover:ring-secondary transition-all duration-500 rounded-md shadow-sm z-10">
                {}
                <div className="absolute top-32 right-0 -mr-8 -mt-12 text-[12rem] font-serif font-bold text-secondary/5 leading-none pointer-events-none transition-transform duration-700 group-hover:-translate-y-4 group-hover:translate-x-4 z-0">
                  {program.id}
                </div>

                {}
                <div className="relative h-64 overflow-hidden shrink-0 z-10 border-b border-border/50">
                  <div className="absolute inset-0 bg-primary/10 z-10 group-hover:bg-transparent transition-colors duration-500" />
                  <img src={program.image} alt={`Tour ${program.title}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>

                {}
                <CardHeader className="relative z-20 pt-8 grow">
                  <CardTitle className="font-serif text-3xl font-medium tracking-wide mb-4 text-primary">
                    {program.title}
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-4 mb-4 text-sm font-sans font-semibold text-secondary uppercase tracking-widest">
                    <span className="flex items-center gap-1.5">
                      <Timer className="size-4" /> {program.duration}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Activity className="size-4" /> {program.level}
                    </span>
                  </div>
                  <CardDescription className="text-base text-muted-foreground font-sans font-light leading-relaxed mt-auto">
                    {program.description}
                  </CardDescription>
                </CardHeader>

                {}
                <CardFooter className="relative z-20 pb-8 pt-0 mt-auto">
                  <Button asChild variant="outline" className="w-full rounded-md border-primary bg-transparent hover:bg-secondary hover:border-secondary hover:text-secondary-foreground text-primary transition-all duration-300 h-12 text-sm uppercase tracking-widest font-semibold">
                    <Link to={program.link}>
                      Select This Tour
                      <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}